/* WinningBundlesRail — homepage "Trending bundles this week" component.
 *
 * Surfaces the top click-through source→target product paths from the
 * cross-division rail so first-time visitors see proven high-value
 * combinations (e.g. iPhone 17 Pro + Cybersecurity Support) before they
 * dive into a single PDP.
 *
 * Behaviour:
 *  - Fetches /api/recommendations/winning-bundles on mount
 *  - Hides itself entirely if 0 bundles (graceful — no awkward empty rail)
 *  - Each card pre-renders both products side-by-side with arrow between
 *  - Click on either side fires the SAME /api/recommendations/click as
 *    the PDP rail, so the loop closes (homepage clicks reinforce the
 *    rail rankings too).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, ShoppingBag, GraduationCap, ArrowRight, Flame, Users } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const KIND_META = {
  systems_service:  { label: 'Systems',  Icon: Cpu,            accent: '#5b8def' },
  services_product: { label: 'Services', Icon: ShoppingBag,    accent: '#c9a227' },
  academy_program:  { label: 'Academy',  Icon: GraduationCap,  accent: '#a875ff' },
};

function Side({ item, onClick }) {
  const meta = KIND_META[item.kind];
  const Icon = meta.Icon;
  const { format } = useCurrency();
  return (
    <Link
      to={item.url}
      onClick={onClick}
      className="group flex-1 flex flex-col min-w-0"
      data-testid={`winning-bundle-side-${item.kind}-${item.slug}`}
    >
      <span className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3 bg-gradient-to-br from-zinc-100 to-zinc-200 grid place-items-center">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <Icon className="w-10 h-10 text-zinc-300" />
        )}
        <span
          className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.18em]"
          style={{ background: `${meta.accent}1a`, color: meta.accent }}
        >
          <Icon className="w-2.5 h-2.5" /> {meta.label}
        </span>
      </span>
      <span className="text-[13.5px] font-bold text-gray-900 group-hover:text-[#c9a227] transition-colors line-clamp-2 leading-snug">
        {item.name}
      </span>
      {typeof item.price === 'number' && item.price > 0 ? (
        <span className="text-[12.5px] font-bold text-gray-900 mt-1">{format(item.price)}</span>
      ) : item.category ? (
        <span className="text-[10.5px] text-gray-400 uppercase tracking-wider mt-1">{item.category}</span>
      ) : null}
    </Link>
  );
}

export default function WinningBundlesRail() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/recommendations/winning-bundles?days=7&limit=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        setBundles(d?.bundles || []);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Fire-and-forget — same shape as CrossDivisionRail.logClick, so a click
  // here also reinforces the boost map for the source PDP.
  const logClick = (b, side) => {
    try {
      fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/recommendations/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_kind: b.source.kind, source_slug: b.source.slug,
          target_kind: side === 'source' ? b.source.kind : b.target.kind,
          target_slug: side === 'source' ? b.source.slug : b.target.slug,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  // Hide the section entirely if we have no winning bundles yet.
  // First-impression rail must NEVER show an empty/awkward state.
  if (loading || bundles.length === 0) return null;

  return (
    <section
      className="py-14 sm:py-20 bg-gradient-to-b from-white via-[#fffaef] to-white"
      data-testid="winning-bundles-rail"
    >
      <div className="zh-container">
        <div className="flex items-center gap-2 mb-2">
          <Flame className="w-4 h-4 text-[#c9a227]" />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">
            Trending bundles · last 7 days
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Most-clicked combinations this week
        </h2>
        <p className="text-[14px] text-gray-500 max-w-2xl mb-8">
          Real visitors paired these up. Tap either side to dive in.
        </p>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="winning-bundles-grid"
        >
          {bundles.map((b, i) => (
            <article
              key={`${b.source.slug}->${b.target.slug}`}
              className="rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#c9a227] hover:shadow-md transition-all"
              data-testid={`winning-bundle-card-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Bundle #{i + 1}</span>
                <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#a8871a]" title={`${b.click_count} clicks · ${b.unique_visitors} unique visitors`}>
                  <Users className="w-3 h-3" />
                  {b.unique_visitors} {b.unique_visitors === 1 ? 'visitor' : 'visitors'}
                </span>
              </div>

              <div className="flex items-stretch gap-3">
                <Side item={b.source} onClick={() => logClick(b, 'source')} />
                <span className="self-center text-gray-300 px-1 shrink-0">
                  <ArrowRight className="w-4 h-4" />
                </span>
                <Side item={b.target} onClick={() => logClick(b, 'target')} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
